import { useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import type { ClaimMetaJson } from '../platform/api';
import { useEmbark } from '../platform/store';
import { WakeProgress, WorldStatusBadge, useWorlds } from '../worlds';
import type { WorldInfoJson } from '../platform/api';
import { useToasts } from '../toasts';
import styles from './embark.module.css';

export function MyWorlds({
  onCreateNew,
  onExpand,
}: {
  onCreateNew: () => void;
  onExpand: (claim: ClaimMetaJson) => void;
}): JSX.Element {
  const myClaims = useEmbark((s) => s.myClaims);
  const fetchMyClaims = useEmbark((s) => s.fetchMyClaims);
  const loadWorlds = useWorlds((s) => s.loadWorlds);
  const worlds = useWorlds((s) => s.worlds);
  const wakeAndPoll = useWorlds((s) => s.wakeAndPoll);
  const pollingWorldId = useWorlds((s) => s.pollingWorldId);
  const pollingStatus = useWorlds((s) => s.pollingStatus);
  const navigate = useNavigate();

  useEffect(() => {
    void fetchMyClaims();
    void loadWorlds();
  }, [fetchMyClaims, loadWorlds]);

  const getWorldForClaim = useCallback(
    (claimId: number): WorldInfoJson | undefined =>
      worlds.find((w) => w.claim_id === claimId),
    [worlds],
  );

  const handlePlay = useCallback(
    async (claim: ClaimMetaJson) => {
      const existing = getWorldForClaim(claim.claim_id);

      if (!existing) {
        useToasts.getState().push('error', 'No world found for this claim');
        return;
      }

      if (existing.status === 'Active') {
        navigate(`/play/${claim.claim_id}`);
        return;
      }

      if (existing.status === 'Hibernating') {
        const world = await wakeAndPoll(existing.world_id);
        if (world) {
          navigate(`/play/${claim.claim_id}`);
        }
      }
    },
    [getWorldForClaim, navigate, wakeAndPoll],
  );

  return (
    <div className={styles.worldsBody}>
      {myClaims.length === 0 ? (
        <div className={styles.worldsEmpty}>
          <div className={styles.worldsEmptyTitle}>
            You haven't founded a settlement yet
          </div>
          <p>Pick a spot on the map and found your settlement.</p>
          <button className={styles.primaryBtn} onClick={onCreateNew}>
            Found a Settlement
          </button>
        </div>
      ) : (
        <>
          <div className={styles.worldsGrid}>
            {myClaims.map((claim) => {
              const world = getWorldForClaim(claim.claim_id);
              const isPolling = world && pollingWorldId === world.world_id;

              return (
                <div key={claim.claim_id} className={styles.worldCard}>
                  <div
                    onClick={() => void handlePlay(claim)}
                    style={{ cursor: 'pointer' }}
                  >
                    <div className={styles.worldCardName}>{claim.name}</div>
                    <div className={styles.worldCardMeta}>
                      {claim.mega_world} &middot; {claim.block_count} block
                      {claim.block_count !== 1 ? 's' : ''}
                      {world && (
                        <>
                          {' '}&middot;{' '}
                          <WorldStatusBadge status={world.status} />
                        </>
                      )}
                    </div>
                  </div>
                  {isPolling && pollingStatus && (
                    <WakeProgress status={pollingStatus} />
                  )}
                  <div className={styles.worldCardActions}>
                    <button
                      className={styles.primaryBtn}
                      style={{ padding: '6px 14px', fontSize: 12 }}
                      onClick={() => void handlePlay(claim)}
                      disabled={!!pollingWorldId}
                    >
                      {!world
                        ? 'Play'
                        : world.status === 'Active'
                          ? 'Connect'
                          : world.status === 'Hibernating'
                            ? 'Wake & Play'
                            : 'Play'}
                    </button>
                    <button
                      className={styles.secondaryBtn}
                      onClick={(e) => {
                        e.stopPropagation();
                        onExpand(claim);
                      }}
                    >
                      Expand
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
          <button className={styles.primaryBtn} onClick={onCreateNew}>
            Found a Settlement
          </button>
        </>
      )}
    </div>
  );
}
